import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import { TID } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home", tid: TID.navHome, end: true },
  { to: "/about", label: "About", tid: TID.navAbout },
  { to: "/techniques", label: "How He Does It", tid: TID.navTechniques },
  { to: "/artwork", label: "Artwork", tid: TID.navGallery },
  { to: "/press", label: "Press", tid: TID.navPress },
  { to: "/commission", label: "Commission", tid: TID.navCommission },
  { to: "/contact", label: "Contact", tid: TID.navContact },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[hsl(var(--bg))]/85 backdrop-blur-md border-b border-[hsl(var(--line))]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            className="group flex items-baseline gap-3"
            data-testid="brand-link"
          >
            <span className="font-serif text-2xl sm:text-3xl leading-none tracking-tight">
              Colin Darke
            </span>
            <span className="hidden sm:block catalog-label">
              The Ambidextrous Artist
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.tid}
                className={({ isActive }) =>
                  `text-sm font-sans tracking-wide ink-underline pb-1 ${
                    isActive
                      ? "text-[hsl(var(--vermillion))]"
                      : "text-[hsl(var(--ink))]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest border border-[hsl(var(--line))] px-3 py-1.5 hover:border-[hsl(var(--ink))] transition-colors ${
                    isActive ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]" : ""
                  }`
                }
              >
                <Lock size={12} /> Admin
              </NavLink>
            )}
          </nav>

          <button
            className="lg:hidden p-2 -mr-2 text-[hsl(var(--ink))]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid={TID.mobileMenuToggle}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[hsl(var(--line))] py-4 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`${l.tid}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-base ${
                    isActive
                      ? "text-[hsl(var(--vermillion))]"
                      : "text-[hsl(var(--ink))]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to={user ? "/admin" : "/admin/login"}
              onClick={() => setOpen(false)}
              data-testid="nav-admin-mobile"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[hsl(var(--ink-muted))]"
            >
              <Lock size={12} /> {user ? "Studio admin" : "Studio login"}
            </NavLink>
          </div>
        )}
      </div>
    </header>
  );
}
